import { z } from 'zod';

/**
 * The `{ error: ... }` failure body, in the two shapes Terra routes send:
 * a bare machine code (`{ error: 'slack_user_not_found' }`) and the object
 * envelope (`{ error: { code, message } }`, see `problemWithError` in
 * `src/http/responder.ts`). `message` is optional because `problemWithError`
 * accepts a code with no message; the code is what a client branches on.
 */
export const ErrorObjectSchema = z.object({
  code: z.string(),
  message: z.string().optional(),
});

export const ErrorFieldSchema = z.union([z.string(), ErrorObjectSchema]);

export const ErrorBodySchema = z.object({ error: ErrorFieldSchema });

/**
 * A failure body read only for its machine code: the legacy `{ error }` shapes above, or a
 * `problem()` body whose top-level `code` carries it. Both fields are optional so one parse
 * accepts either; `errorBodyCode` picks the code.
 */
export const ErrorCodeBodySchema = z.object({
  error: ErrorFieldSchema.optional(),
  code: z.string().optional(),
});

export type ErrorField = z.infer<typeof ErrorFieldSchema>;

/** The machine code of an `error` field, whichever shape carried it. */
export function errorFieldCode(error: ErrorField): string {
  return typeof error === 'string' ? error : error.code;
}

/** The machine code of a failure body: a legacy `error` first (string or `{ code }`), else the top-level `code`. */
export function errorBodyCode(body: z.infer<typeof ErrorCodeBodySchema>): string | undefined {
  return body.error !== undefined ? errorFieldCode(body.error) : body.code;
}
